/**
 * @override
 * Uses King Living InstockProductTile instead of the OOTB ProductTile.
 */
import React, {forwardRef, useRef} from 'react';
import {useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {ChevronLeftIcon, ChevronRightIcon} from '@salesforce/retail-react-app/app/components/icons';
import {
  AspectRatio,
  Box,
  Heading,
  IconButton,
  Skeleton,
  Stack,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import InstockProductTile from '@amblique/king-living/app/components/instock-product-tile';
import {getProductUrl} from '@amblique/king-living/app/components/want-it-sooner';

/**
 * Horizontal product scroller for Einstein recommendations and related product rails.
 */
const ProductScroller = forwardRef(
  (
    {
      header,
      title,
      products,
      isLoading,
      scrollProps,
      itemWidth = {base: '70%', md: '40%', lg: 'calc(33.33% - 10px)'},
      productTileProps,
      ...props
    },
    ref
  ) => {
    const intl = useIntl();
    const scrollRef = useRef();

    if ((!products || products.length < 1) && !isLoading) {
      return null;
    }

    const scroll = (direction = 1) => {
      scrollRef.current?.scrollBy({
        top: 0,
        left: direction * scrollRef.current?.offsetWidth,
        behavior: 'smooth',
      });
    };

    return (
      <Box ref={ref} data-testid="product-scroller" position="relative">
        <Stack spacing={6} {...props}>
          {isLoading && <Skeleton height={6} m="auto" width="150px" />}

          {title && !header && !isLoading && (
            <Heading as="h2" fontSize="xl" textAlign="center">
              {title}
            </Heading>
          )}

          {!title && !isLoading && header}

          <Stack
            ref={scrollRef}
            direction="row"
            overflowX="scroll"
            px={{base: 4, md: 8, lg: 0}}
            py={1}
            spacing={4}
            wrap="nowrap"
            {...scrollProps}
            sx={{
              scrollPadding: {base: 16, md: 32, lg: 0},
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch',
              ...scrollProps?.sx,
            }}
          >
            {isLoading ?
              [0, 1, 2, 3].map((key) => (
                <Box
                  key={key}
                  flex="0 0 auto"
                  style={{scrollSnapAlign: 'start'}}
                  width={itemWidth}
                >
                  <Stack data-testid="product-scroller-item-skeleton">
                    <AspectRatio ratio={1}>
                      <Skeleton />
                    </AspectRatio>
                    <Stack spacing={2}>
                      <Skeleton height={5} width="150px" />
                      <Skeleton height={5} width="75px" />
                    </Stack>
                  </Stack>
                </Box>
              )) :
              products.map((product, idx) => {
                const tileProps =
                  typeof productTileProps === 'function' ?
                    productTileProps(product) :
                    productTileProps;
                const {onClick: onTileClick} = tileProps || {};

                return (
                  <Box
                    key={product?.id || product?.productId || idx}
                    flex="0 0 auto"
                    style={{scrollSnapAlign: 'start'}}
                    width={itemWidth}
                  >
                    <InstockProductTile
                      data-testid="product-scroller-item"
                      product={product}
                      to={getProductUrl(product)}
                      variant="recommendation"
                      onClick={onTileClick}
                    />
                  </Box>
                );
              })}
          </Stack>
        </Stack>

        {!isLoading && products?.length > 3 && (
          <>
            <Box
              left={{base: 0, lg: 4}}
              position="absolute"
              top="50%"
              transform="translateY(-50%)"
              display={{
                base: 'none',
                lg: 'block',
              }}
            >
              <IconButton
                borderRadius="full"
                colorScheme="whiteAlpha"
                data-testid="product-scroller-nav-left"
                icon={<ChevronLeftIcon color="black" />}
                aria-label={intl.formatMessage({
                  id: 'product_scroller.assistive_msg.scroll_left',
                  defaultMessage: 'Scroll products left',
                })}
                onClick={() => scroll(-1)}
              />
            </Box>

            <Box
              position="absolute"
              right={{base: 0, lg: 4}}
              top="50%"
              transform="translateY(-50%)"
              display={{
                base: 'none',
                lg: 'block',
              }}
            >
              <IconButton
                borderRadius="full"
                colorScheme="whiteAlpha"
                data-testid="product-scroller-nav-right"
                icon={<ChevronRightIcon color="black" />}
                aria-label={intl.formatMessage({
                  id: 'product_scroller.assistive_msg.scroll_right',
                  defaultMessage: 'Scroll products right',
                })}
                onClick={() => scroll(1)}
              />
            </Box>
          </>
        )}
      </Box>
    );
  }
);

ProductScroller.displayName = 'ProductScroller';

ProductScroller.propTypes = {
  header: PropTypes.any,
  title: PropTypes.any,
  products: PropTypes.array,
  isLoading: PropTypes.bool,
  scrollProps: PropTypes.object,
  itemWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  productTileProps: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

export default ProductScroller;
